
import { defineComponent, PropType, computed } from 'vue';

interface MetricData {
  totalPOsCreated?: number;
  totalPOsCreatedTrend?: 'up' | 'down' | 'neutral';
  totalValue?: number;
  totalValueTrend?: 'up' | 'down' | 'neutral';
  totalItems?: number;
  totalItemsTrend?: 'up' | 'down' | 'neutral';
  approvalRate?: number; // 0-100 percentage
  approvalRateTrend?: 'up' | 'down' | 'neutral';
  currencySymbol?: string;
}

interface FormattedMetric {
  value: string;
  trendIcon: string;
  trendColor: string;
}

export default defineComponent({
  name: 'SummaryStatistics',
  props: {
    data: {
      type: Object as PropType<MetricData>,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const getTrendDetails = (trend?: 'up' | 'down' | 'neutral') => {
      switch (trend) {
        case 'up':
          return { icon: 'mdi-arrow-up', color: 'green' };
        case 'down':
          return { icon: 'mdi-arrow-down', color: 'red' };
        default:
          return { icon: 'mdi-minus', color: 'grey' }; // Neutral or no trend
      }
    };

    const formattedData = computed(() => {
      const pData = props.data || {};
      const currency = pData.currencySymbol || '$';

      const formatNumber = (num?: number): string => num?.toLocaleString() || '0';
      const formatCurrency = (num?: number): string => `${currency}${num?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
      
      const formatPercentage = (num?: number): string => num !== undefined ? `${num.toFixed(1)}%` : '0.0%';

      const totalPOsCreatedTrendDetails = getTrendDetails(pData.totalPOsCreatedTrend);
      const totalValueTrendDetails = getTrendDetails(pData.totalValueTrend);
      const totalItemsTrendDetails = getTrendDetails(pData.totalItemsTrend);
      const approvalRateTrendDetails = getTrendDetails(pData.approvalRateTrend);

      return {
        totalPOsCreated: {
          value: formatNumber(pData.totalPOsCreated),
          trendIcon: totalPOsCreatedTrendDetails.icon,
          trendColor: totalPOsCreatedTrendDetails.color,
        } as FormattedMetric,
        totalValue: {
          value: formatCurrency(pData.totalValue),
          trendIcon: totalValueTrendDetails.icon,
          trendColor: totalValueTrendDetails.color,
        } as FormattedMetric,
        totalItems: {
          value: formatNumber(pData.totalItems),
          trendIcon: totalItemsTrendDetails.icon,
          trendColor: totalItemsTrendDetails.color,
        } as FormattedMetric,
        approvalRate: {
          value: formatPercentage(pData.approvalRate),
          trendIcon: approvalRateTrendDetails.icon,
          trendColor: approvalRateTrendDetails.color,
        } as FormattedMetric,
      };
    });

    return {
      formattedData,
    };
  },
});
